import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import DtrOfficeTable from '../../components/DtrOfficeTable.jsx';
import BreakdownLog from '../../components/BreakdownLog.jsx';
import InventoryOffice from '../../components/InventoryOffice.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import { ROLES } from '../../config/menuData.js';

const MACHINERY = [
  'Port main engine',
  'Starboard main engine',
  'Auxiliary engine 1',
  'Auxiliary engine 2',
  'Auxiliary engine 3',
  'Steering',
  'Gearbox',
  'Other',
];
const EVENT_TYPES = [
  'Planned Maintenance',
  'Condition-Based Maintenance',
  'Repair',
  'Breakdown',
  'Dry-dock',
  'Bunkering',
  'Survey',
];

/**
 * Maintenance & Bunkering scheduler (FR-MNT / §4.20). Owned by the Maintenance &
 * Bunkering Coordinator (separation of duties): they schedule downtime windows
 * that drop a tug out of dispatch availability. The Dispatcher sees this page
 * read-only (the form is hidden) since downtime drives their availability.
 */
export default function MaintenanceBunkeringPage() {
  const { hasRole } = useAuth();
  const canManage = hasRole(ROLES.MAINT);

  const [tugs, setTugs] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [maint, setMaint] = useState({
    tugId: '',
    eventType: 'Planned Maintenance',
    startTime: '',
    endTime: '',
    notes: '',
    machinery: '',
    cause: '',
  });
  const [logKey, setLogKey] = useState(0); // bumps the breakdown log after any change
  const [maintMsg, setMaintMsg] = useState(null);
  const [savingMaint, setSavingMaint] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  // Recurring plans (FR-MNT-3).
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState({ tugId: '', eventType: 'Planned Maintenance', intervalDays: '90', durationHours: '8', nextDue: '', notes: '' });
  const [planMsg, setPlanMsg] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);

  const loadTimeline = useCallback(async () => {
    setTimeline((await api.getTugTimeline()) || []);
    setLogKey((k) => k + 1);
  }, []);

  const loadPlans = useCallback(async () => {
    setPlans((await api.getMaintenancePlans()) || []);
  }, []);

  const loadAll = useCallback(async () => {
    const [tugsRes] = await Promise.all([api.listTugs(), loadTimeline(), loadPlans()]);
    setTugs(tugsRes || []);
  }, [loadTimeline, loadPlans]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await loadAll();
      } catch (err) {
        setLoadError(err.message || 'Failed to load maintenance scheduler.');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadAll]);

  const submitMaintenance = async (e) => {
    e.preventDefault();
    setMaintMsg(null);
    if (!maint.tugId || !maint.startTime || !maint.endTime) {
      setMaintMsg({ type: 'danger', text: 'Target tug, start and end time are required.' });
      return;
    }
    const isBreakdown = maint.eventType === 'Breakdown';
    if (isBreakdown && (!maint.machinery || !maint.cause.trim())) {
      setMaintMsg({ type: 'danger', text: 'For a breakdown, select the machinery affected and describe the cause.' });
      return;
    }
    setSavingMaint(true);
    try {
      const res = await api.createTugMaintenance(maint.tugId, {
        eventType: maint.eventType,
        startTime: new Date(maint.startTime).toISOString(),
        endTime: new Date(maint.endTime).toISOString(),
        notes: maint.notes.trim() || null,
        ...(isBreakdown ? { machinery: maint.machinery, cause: maint.cause.trim() } : {}),
      });
      await loadTimeline();
      const conflicts = res?.conflicts || [];
      if (conflicts.length > 0) {
        setMaintMsg({
          type: 'warning',
          text: `Downtime scheduled, but it conflicts with active job(s): ${conflicts
            .map((c) => c.bookingRef)
            .join(', ')}. Review the scheduling board.`,
        });
      } else {
        setMaintMsg({ type: 'success', text: 'Maintenance downtime scheduled — tug marked unavailable on the board.' });
      }
      setMaint({ ...maint, startTime: '', endTime: '', notes: '' });
    } catch (err) {
      setMaintMsg({ type: 'danger', text: err.message || 'Failed to schedule downtime.' });
    } finally {
      setSavingMaint(false);
    }
  };

  const submitPlan = async (e) => {
    e.preventDefault();
    setPlanMsg(null);
    if (!plan.tugId || !plan.nextDue || !(Number(plan.intervalDays) > 0)) {
      setPlanMsg({ type: 'danger', text: 'Target tug, interval (days) and first due date are required.' });
      return;
    }
    setSavingPlan(true);
    try {
      await api.createMaintenancePlan({
        tugId: plan.tugId,
        eventType: plan.eventType,
        intervalDays: Number(plan.intervalDays),
        durationHours: Number(plan.durationHours) || 8,
        nextDue: new Date(plan.nextDue).toISOString(),
        notes: plan.notes.trim() || null,
      });
      await Promise.all([loadPlans(), loadTimeline()]);
      setPlanMsg({ type: 'success', text: 'Recurring plan saved — due windows auto-generate ahead of time.' });
      setPlan({ ...plan, nextDue: '', notes: '' });
    } catch (err) {
      setPlanMsg({ type: 'danger', text: err.message || 'Failed to save plan.' });
    } finally {
      setSavingPlan(false);
    }
  };

  const [bunker, setBunker] = useState(null); // { tugId, eventId, litres, supplier }

  const completeWindow = async (tugId, eventId, delivery) => {
    setMaintMsg(null);
    setCompletingId(eventId);
    try {
      const res = await api.completeTugMaintenance(tugId, eventId, delivery);
      await loadTimeline();
      setMaintMsg({
        type: 'success',
        text: res?.returnedToService
          ? 'Window closed — tug marked Ready for Operation and back in dispatch availability.'
          : 'Window closed. Tug stays unavailable: another active downtime window is still open.',
      });
    } catch (err) {
      setMaintMsg({ type: 'danger', text: err.message || 'Failed to mark the window complete.' });
    } finally {
      setCompletingId(null);
    }
  };

  const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString() : '—');

  return (
    <div className="container-fluid py-4" data-testid="maintenance-bunkering-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-wrench me-2" aria-hidden="true" />
                Maintenance &amp; Bunkering Scheduler
              </h3>
              <p className="text-muted mb-0 small">
                Schedule maintenance, repair, dry-dock, bunkering and survey windows. Active windows
                remove a tug from dispatch availability.
              </p>
            </div>
            {!canManage && (
              <span className="badge bg-secondary p-2" data-testid="maint-readonly">
                <i className="fas fa-eye me-1" aria-hidden="true" />
                Read-only (Dispatcher)
              </span>
            )}
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="maint-load-error">
          {loadError}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* Coordinator-only: schedule a window */}
          {canManage && (
            <div className="col-lg-4 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white pt-4 border-bottom-0">
                  <h5 className="mb-0 text-secondary">
                    <i className="fas fa-tools me-2" aria-hidden="true" />
                    Schedule Downtime
                  </h5>
                </div>
                <div className="card-body">
                  {maintMsg && (
                    <div className={`alert alert-${maintMsg.type}`} role="alert" data-testid="maint-message">
                      {maintMsg.text}
                    </div>
                  )}
                  <form onSubmit={submitMaintenance} noValidate>
                    <div className="mb-3">
                      <label htmlFor="maint-tug" className="form-label small fw-bold text-muted">
                        Target Tug <span className="text-danger">*</span>
                      </label>
                      <select
                        id="maint-tug"
                        className="form-select form-select-sm"
                        value={maint.tugId}
                        onChange={(e) => setMaint({ ...maint, tugId: e.target.value })}
                        data-testid="maint-tug"
                      >
                        <option value="">Select a tug…</option>
                        {tugs.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.class_type} - {Number(t.bollard_pull_tons)} T)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="maint-type" className="form-label small fw-bold text-muted">
                        Event Type <span className="text-danger">*</span>
                      </label>
                      <select
                        id="maint-type"
                        className="form-select form-select-sm"
                        value={maint.eventType}
                        onChange={(e) => setMaint({ ...maint, eventType: e.target.value })}
                        data-testid="maint-type"
                      >
                        {EVENT_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                    {maint.eventType === 'Breakdown' && (
                      <div className="row">
                        <div className="col-5 mb-3">
                          <label htmlFor="maint-machinery" className="form-label small fw-bold text-muted">
                            Machinery <span className="text-danger">*</span>
                          </label>
                          <select
                            id="maint-machinery"
                            className="form-select form-select-sm"
                            value={maint.machinery}
                            onChange={(e) => setMaint({ ...maint, machinery: e.target.value })}
                            data-testid="maint-machinery"
                          >
                            <option value="">Select…</option>
                            {MACHINERY.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-7 mb-3">
                          <label htmlFor="maint-cause" className="form-label small fw-bold text-muted">
                            Cause of breakdown <span className="text-danger">*</span>
                          </label>
                          <input
                            id="maint-cause"
                            className="form-control form-control-sm"
                            maxLength={1000}
                            value={maint.cause}
                            onChange={(e) => setMaint({ ...maint, cause: e.target.value })}
                            data-testid="maint-cause"
                          />
                        </div>
                      </div>
                    )}
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label htmlFor="maint-start" className="form-label small fw-bold text-muted">
                          Start Time <span className="text-danger">*</span>
                        </label>
                        <input
                          id="maint-start"
                          type="datetime-local"
                          className="form-control form-control-sm"
                          value={maint.startTime}
                          onChange={(e) => setMaint({ ...maint, startTime: e.target.value })}
                          data-testid="maint-start"
                        />
                      </div>
                      <div className="col-6 mb-3">
                        <label htmlFor="maint-end" className="form-label small fw-bold text-muted">
                          {maint.eventType === 'Breakdown' ? 'Expected back in service' : 'End Time'} <span className="text-danger">*</span>
                        </label>
                        <input
                          id="maint-end"
                          type="datetime-local"
                          className="form-control form-control-sm"
                          value={maint.endTime}
                          onChange={(e) => setMaint({ ...maint, endTime: e.target.value })}
                          data-testid="maint-end"
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="maint-notes" className="form-label small fw-bold text-muted">
                        Details &amp; Notes
                      </label>
                      <textarea
                        id="maint-notes"
                        rows={2}
                        className="form-control form-control-sm"
                        value={maint.notes}
                        onChange={(e) => setMaint({ ...maint, notes: e.target.value })}
                        data-testid="maint-notes"
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm w-100"
                      disabled={savingMaint}
                      data-testid="schedule-maint"
                    >
                      {savingMaint && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-wrench me-1" aria-hidden="true" />
                      Schedule Downtime
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Scheduling board (both roles) */}
          <div className={canManage ? 'col-lg-8 mb-4' : 'col-12 mb-4'}>
            <div className="card border-0 shadow-sm h-100" data-testid="scheduling-board">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-calendar-week me-2" aria-hidden="true" />
                  Fleet Downtime Board
                </h5>
                <p className="text-muted small mb-0">
                  Maintenance/bunkering windows mark a tug unavailable; a conflict is flagged when a
                  window overlaps an active job booking.
                </p>
              </div>
              <div className="card-body">
                {timeline.length === 0 ? (
                  <p className="text-muted small mb-0">No tugs to schedule.</p>
                ) : (
                  timeline.map((row) => (
                    <div
                      key={row.tugId}
                      className="d-flex align-items-center border-bottom py-2"
                      data-testid="board-row"
                      data-name={row.name}
                    >
                      <div className="fw-bold" style={{ minWidth: 160 }}>
                        {row.name}
                        {row.hasConflict && (
                          <span className="badge bg-danger ms-2" data-testid="board-conflict">
                            <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />
                            Conflict
                          </span>
                        )}
                      </div>
                      <div className="d-flex flex-wrap gap-2 flex-grow-1">
                        {row.maintenanceWindows.length === 0 && row.activeJobs.length === 0 && (
                          <span className="badge bg-success-subtle text-success border border-success">
                            Available
                          </span>
                        )}
                        {row.activeJobs.map((j) => (
                          <span
                            key={j.jobId}
                            className="badge bg-info text-dark"
                            title={new Date(j.scheduledAt).toLocaleString()}
                            data-testid="board-job"
                          >
                            <i className="fas fa-briefcase me-1" aria-hidden="true" />
                            {j.bookingRef}
                          </span>
                        ))}
                        {row.maintenanceWindows.map((w) =>
                          w.completed ? (
                            <span
                              key={w.id}
                              className="badge bg-success-subtle text-success border border-success"
                              title={`Completed ${
                                w.completedAt ? new Date(w.completedAt).toLocaleString() : ''
                              }${w.completedBy ? ` by ${w.completedBy}` : ''}`}
                              data-testid="board-window-done"
                            >
                              <i className="fas fa-circle-check me-1" aria-hidden="true" />
                              Ready · {w.eventType}
                            </span>
                          ) : (
                            <span
                              key={w.id}
                              className="d-inline-flex align-items-center gap-1"
                              data-testid="board-window"
                            >
                              <span
                                className={`badge ${w.conflict ? 'bg-danger' : 'bg-warning text-dark'}`}
                                title={`${new Date(w.startTime).toLocaleString()} → ${new Date(
                                  w.endTime
                                ).toLocaleString()}`}
                              >
                                <i className="fas fa-wrench me-1" aria-hidden="true" />
                                Unavailable · {w.eventType}
                              </span>
                              {canManage && (
                                <button
                                  type="button"
                                  className="btn btn-outline-success btn-sm py-0 px-1"
                                  onClick={() =>
                                    w.eventType === 'Bunkering'
                                      ? setBunker({ tugId: row.tugId, eventId: w.id, litres: '', supplier: '' })
                                      : completeWindow(row.tugId, w.id)
                                  }
                                  disabled={completingId === w.id}
                                  title="Close this window and return the tug to service"
                                  data-testid="complete-window"
                                >
                                  {completingId === w.id ? (
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <>
                                      <i className="fas fa-circle-check me-1" aria-hidden="true" />
                                      Return to Service
                                    </>
                                  )}
                                </button>
                              )}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          {/* Recurring maintenance plans (FR-MNT-3) */}
          <div className="col-12 mt-1" data-testid="maint-plans">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-rotate me-2" aria-hidden="true" />
                  Recurring Maintenance Plans
                </h5>
                <p className="text-muted small mb-0">
                  Interval-based plans auto-generate the next downtime window ahead of its due date.
                </p>
              </div>
              <div className="card-body">
                <div className="row">
                  {canManage && (
                    <div className="col-lg-4 mb-3">
                      {planMsg && (
                        <div className={`alert alert-${planMsg.type} py-2`} role="alert" data-testid="plan-message">
                          {planMsg.text}
                        </div>
                      )}
                      <form onSubmit={submitPlan} noValidate className="border rounded p-3">
                        <div className="mb-2">
                          <label htmlFor="plan-tug" className="form-label small fw-bold text-muted">Tug</label>
                          <select id="plan-tug" className="form-select form-select-sm" value={plan.tugId}
                            onChange={(e) => setPlan({ ...plan, tugId: e.target.value })} data-testid="plan-tug">
                            <option value="">Select a tug…</option>
                            {tugs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        </div>
                        <div className="mb-2">
                          <label htmlFor="plan-type" className="form-label small fw-bold text-muted">Event Type</label>
                          <select id="plan-type" className="form-select form-select-sm" value={plan.eventType}
                            onChange={(e) => setPlan({ ...plan, eventType: e.target.value })} data-testid="plan-type">
                            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div className="row">
                          <div className="col-6 mb-2">
                            <label htmlFor="plan-interval" className="form-label small fw-bold text-muted">Every (days)</label>
                            <input id="plan-interval" type="number" min="1" className="form-control form-control-sm"
                              value={plan.intervalDays} onChange={(e) => setPlan({ ...plan, intervalDays: e.target.value })} data-testid="plan-interval" />
                          </div>
                          <div className="col-6 mb-2">
                            <label htmlFor="plan-duration" className="form-label small fw-bold text-muted">Hours</label>
                            <input id="plan-duration" type="number" min="1" className="form-control form-control-sm"
                              value={plan.durationHours} onChange={(e) => setPlan({ ...plan, durationHours: e.target.value })} data-testid="plan-duration" />
                          </div>
                        </div>
                        <div className="mb-2">
                          <label htmlFor="plan-next" className="form-label small fw-bold text-muted">First Due</label>
                          <input id="plan-next" type="datetime-local" className="form-control form-control-sm"
                            value={plan.nextDue} onChange={(e) => setPlan({ ...plan, nextDue: e.target.value })} data-testid="plan-next" />
                        </div>
                        <button type="submit" className="btn btn-primary btn-sm w-100" disabled={savingPlan} data-testid="save-plan">
                          {savingPlan && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                          <i className="fas fa-rotate me-1" aria-hidden="true" />Save Plan
                        </button>
                      </form>
                    </div>
                  )}
                  <div className={canManage ? 'col-lg-8' : 'col-12'}>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0 small" data-testid="plans-table">
                        <thead className="bg-light text-secondary text-uppercase">
                          <tr><th>Tug</th><th>Type</th><th className="text-center">Every</th><th className="text-center">Hours</th><th>Next Due</th></tr>
                        </thead>
                        <tbody>
                          {plans.length === 0 ? (
                            <tr><td colSpan={5} className="text-center text-muted py-3" data-testid="plans-empty">No recurring plans.</td></tr>
                          ) : (
                            plans.map((p) => (
                              <tr key={p.id} data-testid="plan-row">
                                <td className="fw-semibold">{p.tugName}</td>
                                <td>{p.eventType}</td>
                                <td className="text-center">{p.intervalDays}d</td>
                                <td className="text-center">{p.durationHours}h</td>
                                <td>{fmtDate(p.nextDue)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {bunker && (
        <div className="card border-primary mb-3" data-testid="bunker-prompt">
          <div className="card-body row g-2 align-items-end">
            <div className="col-md-3">
              <label htmlFor="bunker-litres" className="form-label small fw-bold text-muted">Litres delivered</label>
              <input id="bunker-litres" type="number" min="0" className="form-control form-control-sm" value={bunker.litres} onChange={(e) => setBunker({ ...bunker, litres: e.target.value })} data-testid="bunker-litres" />
            </div>
            <div className="col-md-4">
              <label htmlFor="bunker-supplier" className="form-label small fw-bold text-muted">Supplier</label>
              <input id="bunker-supplier" className="form-control form-control-sm" value={bunker.supplier} onChange={(e) => setBunker({ ...bunker, supplier: e.target.value })} data-testid="bunker-supplier" />
            </div>
            <div className="col-md-5">
              <button
                className="btn btn-primary btn-sm me-2"
                onClick={async () => {
                  const delivery = {};
                  if (bunker.litres !== '') delivery.fuelDeliveredL = Number(bunker.litres);
                  if (bunker.supplier.trim()) delivery.fuelSupplier = bunker.supplier.trim();
                  await completeWindow(bunker.tugId, bunker.eventId, delivery);
                  setBunker(null);
                }}
                data-testid="bunker-confirm"
              >
                Mark bunkering complete
              </button>
              <button className="btn btn-link btn-sm" onClick={() => setBunker(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-4">
        <InventoryOffice />
      </div>
      <div className="mt-4">
        <BreakdownLog refreshKey={logKey} />
      </div>
      <div className="mt-4">
        <DtrOfficeTable />
      </div>
    </div>
  );
}
